import { useState, useEffect, useRef } from "react";
import {
  Link2, RefreshCw, LayoutGrid, BarChart3, ArrowRight,
  Check, Copy, Star, TrendingDown, Crown, ChevronRight,
  ShoppingCart, Zap, Mail
} from "lucide-react";

const STEPS = [
  {
    id: "import",
    label: "Import",
    icon: Link2,
    title: "Paste Amazon URL",
    subtitle: "ASIN extracted automatically from any link format",
  },
  {
    id: "sync",
    label: "Sync",
    icon: RefreshCw,
    title: "Auto-Sync Data",
    subtitle: "Price, rating, stock & images refresh every 8 hours",
  },
  {
    id: "display",
    label: "Display",
    icon: LayoutGrid,
    title: "Beautiful Product Cards",
    subtitle: "Responsive cards, grids, shortcodes & Gutenberg blocks",
  },
  {
    id: "pro",
    label: "PRO",
    icon: Crown,
    title: "PRO Power Tools",
    subtitle: "Analytics, alerts, Elementor, CLI & CSV import",
  },
];

/* ── Fake data for the animated screens ────────────────── */
const DEMO_PRODUCT = {
  title: "Sony WH-1000XM5 Wireless Headphones",
  price: "$278.00",
  oldPrice: "$399.99",
  rating: 4.7,
  reviews: "12,847",
  asin: "B09XS7JWHH",
  image: null, // will use a placeholder
  availability: "In Stock",
  domain: "amazon.com",
  discount: "30%",
};

const PRICE_HISTORY = [68, 72, 70, 65, 62, 58, 55, 60, 52, 48, 45, 42];

export default function AnimatedDemo() {
  const [activeStep, setActiveStep] = useState(0);
  const [typing, setTyping] = useState("");
  const [imported, setImported] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const timerRef = useRef(null);

  const AMAZON_URL = "https://www.amazon.com/dp/B09XS7JWHH";

  // Auto-advance steps
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % STEPS.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Reset animations when step changes
  useEffect(() => {
    setTyping("");
    setImported(false);
    setSyncing(false);
    setSyncDone(false);

    if (activeStep === 0) {
      // Typing animation for import step
      let i = 0;
      const typeInterval = setInterval(() => {
        if (i <= AMAZON_URL.length) {
          setTyping(AMAZON_URL.slice(0, i));
          i++;
        } else {
          clearInterval(typeInterval);
          setTimeout(() => setImported(true), 400);
        }
      }, 45);
      return () => clearInterval(typeInterval);
    }
    if (activeStep === 1) {
      setSyncing(true);
      setTimeout(() => {
        setSyncing(false);
        setSyncDone(true);
      }, 2500);
    }
  }, [activeStep]);

  const handleStepClick = (idx) => {
    clearInterval(timerRef.current);
    setActiveStep(idx);
    timerRef.current = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % STEPS.length);
    }, 5000);
  };

  return (
    <section
      data-testid="animated-demo-section"
      className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-[#09090B] relative overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#FF7300]/[0.06] rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-[#FF7300]/15 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            See It in Action
          </span>
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tight mb-3">
            From Amazon Link to Revenue
          </h2>
          <p className="text-[#A1A1AA] text-base max-w-lg mx-auto">
            Watch how AmznAWP transforms a simple Amazon URL into a conversion-optimized product display in seconds.
          </p>
        </div>

        {/* Step tabs */}
        <div className="flex justify-center gap-2 mb-10" data-testid="demo-step-tabs">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === activeStep;
            return (
              <button
                key={step.id}
                data-testid={`demo-tab-${step.id}`}
                onClick={() => handleStepClick(idx)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border-0 cursor-pointer ${
                  isActive
                    ? "bg-[#FF7300] text-white shadow-[0_0_20px_rgba(255,115,0,0.3)]"
                    : "bg-[#18181B] text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                }`}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{step.label}</span>
                {isActive && <ChevronRight size={12} className="animate-pulse" />}
              </button>
            );
          })}
        </div>

        {/* Demo viewport */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-xl overflow-hidden shadow-2xl">
          {/* Window chrome */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#27272A] bg-[#111113]">
            <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
            <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
            <div className="w-3 h-3 rounded-full bg-[#28C840]" />
            <div className="flex-1 mx-4">
              <div className="bg-[#27272A] rounded-md px-3 py-1 text-xs text-[#71717A] font-mono max-w-xs">
                yoursite.com/wp-admin
              </div>
            </div>
          </div>

          {/* Content area */}
          <div className="p-6 md:p-8 min-h-[340px] flex flex-col justify-center">
            {/* Step 0: Import */}
            {activeStep === 0 && (
              <div className="animate-fade-in-up" data-testid="demo-screen-import">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded bg-[#FF7300] flex items-center justify-center">
                    <Link2 size={12} className="text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Import Product</span>
                </div>

                {/* URL input */}
                <div className="bg-[#09090B] border border-[#27272A] rounded-lg p-4 mb-4 flex items-center gap-3">
                  <Copy size={14} className="text-[#71717A] flex-shrink-0" />
                  <span className="font-mono text-sm text-[#FF7300] flex-1 truncate">
                    {typing}
                    <span className="inline-block w-0.5 h-4 bg-[#FF7300] ml-0.5 animate-pulse align-middle" />
                  </span>
                </div>

                {/* Import result */}
                <div
                  className={`transition-all duration-500 ${
                    imported ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                >
                  <div className="bg-[#09090B] border border-[#27272A] rounded-lg p-4 flex gap-4">
                    {/* Product thumbnail placeholder */}
                    <div className="w-16 h-16 rounded-md bg-gradient-to-br from-[#27272A] to-[#18181B] flex items-center justify-center flex-shrink-0">
                      <ShoppingCart size={20} className="text-[#52525B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Check size={14} className="text-[#22c55e]" />
                        <span className="text-[#22c55e] text-xs font-semibold">Imported Successfully</span>
                      </div>
                      <p className="text-white text-sm font-semibold truncate">{DEMO_PRODUCT.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[#FF7300] font-bold text-sm">{DEMO_PRODUCT.price}</span>
                        <span className="text-[#71717A] line-through text-xs">{DEMO_PRODUCT.oldPrice}</span>
                        <span className="bg-[#22c55e]/10 text-[#22c55e] text-[10px] font-bold px-2 py-0.5 rounded-full">-{DEMO_PRODUCT.discount}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Sync */}
            {activeStep === 1 && (
              <div className="animate-fade-in-up" data-testid="demo-screen-sync">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-6 h-6 rounded bg-[#2563EB] flex items-center justify-center">
                    <RefreshCw size={12} className="text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Auto-Sync Dashboard</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  {[
                    { label: "Products", value: "47", color: "text-white" },
                    { label: "Synced", value: syncDone ? "47" : syncing ? "23" : "0", color: "text-[#22c55e]" },
                    { label: "Errors", value: "0", color: "text-[#ef4444]" },
                    { label: "Next Sync", value: "6h 12m", color: "text-[#93c5fd]" },
                  ].map((s) => (
                    <div key={s.label} className="bg-[#09090B] border border-[#27272A] rounded-lg p-3 text-center">
                      <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Sync progress bar */}
                <div className="bg-[#09090B] border border-[#27272A] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#A1A1AA]">
                      {syncDone ? "Sync Complete" : syncing ? "Syncing products..." : "Preparing sync..."}
                    </span>
                    <span className="text-xs text-[#FF7300] font-semibold">
                      {syncDone ? "100%" : syncing ? "49%" : "0%"}
                    </span>
                  </div>
                  <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF7300] to-[#FF9500] rounded-full transition-all duration-[2500ms] ease-out"
                      style={{ width: syncDone ? "100%" : syncing ? "49%" : "0%" }}
                    />
                  </div>
                  {syncDone && (
                    <div className="flex items-center gap-1.5 mt-2 animate-fade-in-up">
                      <Check size={12} className="text-[#22c55e]" />
                      <span className="text-xs text-[#22c55e]">47 products synced, 0 errors</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Display */}
            {activeStep === 2 && (
              <div className="animate-fade-in-up" data-testid="demo-screen-display">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-6 h-6 rounded bg-[#22c55e] flex items-center justify-center">
                    <LayoutGrid size={12} className="text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">Frontend Product Card</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Product card preview */}
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                    <div className="bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef] p-6 flex items-center justify-center h-40">
                      <div className="w-20 h-20 rounded-full bg-[#FF7300]/10 flex items-center justify-center">
                        <ShoppingCart size={28} className="text-[#FF7300]" />
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-[#09090B] text-sm font-semibold leading-snug mb-2">{DEMO_PRODUCT.title}</p>
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} size={12} className={s <= 4 ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#F59E0B] fill-[#F59E0B] opacity-50"} />
                        ))}
                        <span className="text-[10px] text-[#71717A] ml-1">{DEMO_PRODUCT.rating} ({DEMO_PRODUCT.reviews})</span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[#B12704] font-bold text-lg">{DEMO_PRODUCT.price}</span>
                        <span className="text-[#71717A] line-through text-xs">{DEMO_PRODUCT.oldPrice}</span>
                        <span className="bg-[#B12704] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">-{DEMO_PRODUCT.discount}</span>
                      </div>
                      <div className="bg-[#FF9900] text-white text-center py-2 rounded text-xs font-bold">
                        Buy on Amazon
                      </div>
                    </div>
                  </div>

                  {/* Price history chart */}
                  <div className="bg-[#09090B] border border-[#27272A] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-[#A1A1AA] font-semibold">Price History (90d)</span>
                      <span className="flex items-center gap-1 text-[#22c55e] text-xs font-semibold">
                        <TrendingDown size={12} /> -38%
                      </span>
                    </div>
                    {/* SVG sparkline */}
                    <svg viewBox="0 0 200 60" className="w-full h-20">
                      <defs>
                        <linearGradient id="chartGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#FF7300" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#FF7300" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d={`M0,${60 - PRICE_HISTORY[0] * 0.8} ${PRICE_HISTORY.map((v, i) => `L${(i / (PRICE_HISTORY.length - 1)) * 200},${60 - v * 0.8}`).join(" ")} L200,60 L0,60 Z`}
                        fill="url(#chartGrad)"
                      />
                      <polyline
                        points={PRICE_HISTORY.map((v, i) => `${(i / (PRICE_HISTORY.length - 1)) * 200},${60 - v * 0.8}`).join(" ")}
                        fill="none"
                        stroke="#FF7300"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      {/* Current price dot */}
                      <circle cx="200" cy={60 - PRICE_HISTORY[PRICE_HISTORY.length - 1] * 0.8} r="4" fill="#FF7300" className="animate-pulse" />
                    </svg>
                    <div className="flex justify-between text-[10px] text-[#52525B] mt-1">
                      <span>90 days ago</span>
                      <span>Today: {DEMO_PRODUCT.price}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: PRO */}
            {activeStep === 3 && (
              <div className="animate-fade-in-up" data-testid="demo-screen-pro">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-6 h-6 rounded bg-[#FF7300] flex items-center justify-center">
                    <Crown size={12} className="text-white" />
                  </div>
                  <span className="text-white font-semibold text-sm">PRO Features</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    {
                      icon: BarChart3,
                      title: "Click Analytics",
                      desc: "1,247 clicks this month",
                      accent: "#2563EB",
                      stat: "+23%",
                    },
                    {
                      icon: Mail,
                      title: "Price Drop Alert",
                      desc: "3 products dropped today",
                      accent: "#22c55e",
                      stat: "-$42",
                    },
                    {
                      icon: Zap,
                      title: "Smart Sync",
                      desc: "High-click products first",
                      accent: "#FF7300",
                      stat: "2x faster",
                    },
                  ].map((feat, i) => {
                    const Icon = feat.icon;
                    return (
                      <div
                        key={feat.title}
                        className="bg-[#09090B] border border-[#27272A] rounded-lg p-4 hover:border-[#FF7300]/40 transition-colors animate-fade-in-up"
                        style={{ animationDelay: `${i * 150}ms` }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: feat.accent + "20" }}>
                            <Icon size={16} style={{ color: feat.accent }} />
                          </div>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: feat.accent, backgroundColor: feat.accent + "15" }}>
                            {feat.stat}
                          </span>
                        </div>
                        <p className="text-white text-sm font-semibold mb-1">{feat.title}</p>
                        <p className="text-[#71717A] text-xs">{feat.desc}</p>
                      </div>
                    );
                  })}
                </div>

                {/* CLI preview */}
                <div className="mt-4 bg-[#09090B] border border-[#27272A] rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] text-[#71717A] uppercase tracking-wider font-semibold">WP-CLI</span>
                  </div>
                  <pre className="font-mono text-xs text-[#A1A1AA] leading-relaxed">
                    <span className="text-[#22c55e]">$</span> wp affpro sync --batch=50{"\n"}
                    <span className="text-[#71717A]">Syncing 50 products... </span><span className="text-[#22c55e]">Done: 50 synced, 0 failed.</span>{"\n"}
                    <span className="text-[#22c55e]">$</span> wp affpro csv-import products.csv{"\n"}
                    <span className="text-[#71717A]">Found 128 rows. Importing... </span><span className="text-[#22c55e]">Done: 124 imported, 4 skipped.</span>
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step description below */}
        <div className="text-center mt-8">
          <h3 className="text-white font-display font-bold text-lg mb-1">
            {STEPS[activeStep].title}
          </h3>
          <p className="text-[#71717A] text-sm">
            {STEPS[activeStep].subtitle}
          </p>
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-8">
          <a
            href="#pricing"
            data-testid="demo-cta-link"
            className="flex items-center gap-2 bg-[#FF7300] text-white hover:bg-[#E66800] transition-all rounded-full px-7 py-3 text-sm font-bold no-underline shadow-[0_0_30px_rgba(255,115,0,0.2)] hover:shadow-[0_0_40px_rgba(255,115,0,0.35)]"
          >
            Get Started <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
